import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { MultiStep, StepProps } from '@org/ui';
import { StudentForm, StudentFormRef } from '../forms/student';
import { PersonForm, PersonFormRef } from '../forms/person';
import { StudentIdentityStep, StudentIdentityStepRef } from './student-identity-step';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@org/ui';
import { trpc } from '@org/clients/school';
import { unwrapTrpcData } from '@org/ui';
import { useTranslation } from 'react-i18next';
import { resolveAfterSave, useStepNavigation } from '../shared/step-navigation';
import { UnsavedChangesDialog } from '../shared/unsaved-changes-dialog';

type StudentMultiStepFormProps = {
  defaultValues: any;
  onSubmit: () => void;
  onClose: () => void;
  isEditing: boolean;
};

const STATES = ["student", "person", "identity"] as const;

/**
 * Student → personal details → identity.
 * Creating is a wizard (the first step creates the user, so the others unlock
 * only after it is saved); editing lets the admin click any step. Each step
 * keeps its own explicit Save; leaving a step with unsaved edits asks first.
 */
export const StudentMultiStepForm: React.FC<StudentMultiStepFormProps> = ({
  defaultValues,
  onSubmit,
  onClose,
  isEditing
}) => {
  const { t } = useTranslation('admin');
  const steps: StepProps[] = useMemo(
    () => [
      { title: t('students.multiStep.steps.studentInfo') },
      { title: t('students.multiStep.steps.personalDetails') },
      { title: t('students.multiStep.steps.identity') },
    ],
    [t],
  );
  const stepLabels = useMemo(
    () => ({
      completed: t('common.steps.completed'),
      current: t('common.steps.current'),
      pending: t('common.steps.pending'),
    }),
    [t],
  );

  const nav = useStepNavigation(STATES.length, isEditing);
  const currentState = STATES[nav.currentIndex];

  const [studentData, setStudentData] = useState<Record<string, any>>(defaultValues || {});
  const [personData, setPersonData] = useState<Record<string, any>>({});

  const studentFormRef = useRef<StudentFormRef>(null);
  const personFormRef = useRef<PersonFormRef>(null);
  const identityStepRef = useRef<StudentIdentityStepRef>(null);

  // Unsaved edits on the step being shown, and where the admin wanted to go.
  const [stepDirty, setStepDirty] = useState(false);
  const [pendingTarget, setPendingTarget] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Bumped to remount the current step and drop its edits ("discard").
  const [stepKey, setStepKey] = useState(0);

  const userId = defaultValues?.userId;
  const { data: personDataFromDb } =
    trpc.persons.getByUserId.useQuery(userId, {
      enabled: isEditing && !!userId
    });

  useEffect(() => {
    const unwrapped = unwrapTrpcData(personDataFromDb);
    if (unwrapped) {
      setPersonData(unwrapped);
    }
  }, [personDataFromDb]);

  // A freshly shown step starts clean.
  useEffect(() => {
    setStepDirty(false);
  }, [nav.currentIndex, stepKey]);

  const onDirtyChange = useCallback((dirty: boolean) => setStepDirty(dirty), []);

  /** Where to land once the current step was saved. */
  const afterSave = (): number | null => {
    const target = resolveAfterSave(pendingTarget, nav.currentIndex, STATES.length);
    setPendingTarget(null);
    setDialogOpen(false);
    return target;
  };

  const moveTo = (target: number) => {
    if (nav.mode === 'wizard' && target === nav.currentIndex + 1) {
      nav.next(); // unlocks the step as well
    } else {
      nav.goTo(target);
    }
  };

  const saveStudent = (data: Record<string, any>) => {
    setStudentData({ ...studentData, ...data });
    const target = afterSave();
    if (target !== null) moveTo(target);
  };

  const savePerson = (data: Record<string, any>) => {
    setPersonData({ ...personData, ...data });
    const target = afterSave();
    if (target !== null) moveTo(target);
  };

  // Last step: its own save already wrote the two fields. Finishing closes
  // the form — unless the admin was on the way to another step.
  const finishIdentity = () => {
    if (pendingTarget !== null) {
      const target = afterSave();
      if (target !== null) moveTo(target);
      return;
    }
    onSubmit();
  };

  const requestStep = (target: number) => {
    if (target === nav.currentIndex || !nav.canReach(target)) return;
    if (stepDirty) {
      setPendingTarget(target);
      setDialogOpen(true);
      return;
    }
    nav.goTo(target);
  };

  const submitCurrentStep = () => {
    if (currentState === 'student') studentFormRef.current?.submit();
    else if (currentState === 'person') personFormRef.current?.submit();
    else identityStepRef.current?.submit();
  };

  const discardAndGo = () => {
    const target = pendingTarget;
    setPendingTarget(null);
    setDialogOpen(false);
    setStepKey((k) => k + 1);
    if (target !== null) nav.goTo(target);
  };

  const stayHere = () => {
    setPendingTarget(null);
    setDialogOpen(false);
  };

  return (
    <div className="flex flex-col w-full min-h-screen bg-gray-50">
      <div className="w-full px-8 py-4 bg-white border-b border-gray-200 shadow-sm">
        <MultiStep
          steps={steps}
          currentStep={nav.currentIndex}
          onChange={requestStep}
          mode={nav.mode}
          reachable={nav.reachable}
          labels={stepLabels}
        />
      </div>

      <div className="w-full flex-grow px-6 pt-6 pb-28 sm:pb-6 max-w-5xl mx-auto mt-6">
        {currentState === "student" && (
          <Card key={`student-${stepKey}`}>
            <CardHeader>
              <CardTitle>{t('students.multiStep.student.title')}</CardTitle>
              <CardDescription>{t('students.multiStep.student.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <StudentForm
                ref={studentFormRef}
                defaultValues={studentData}
                onSubmit={saveStudent}
                onClose={onClose}
                onDirtyChange={onDirtyChange}
              />
            </CardContent>
          </Card>
        )}
        {currentState === "person" && (
          <Card key={`person-${stepKey}`}>
            <CardHeader>
              <CardTitle>{t('students.multiStep.person.title')}</CardTitle>
              <CardDescription>{t('students.multiStep.person.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <PersonForm
                defaultValues={{
                  ...personData,
                  userId: studentData.userId
                }}
                onSubmit={savePerson}
                onClose={onClose}
                hideButtons={false}
                ref={personFormRef}
                onDirtyChange={onDirtyChange}
              />
            </CardContent>
          </Card>
        )}
        {currentState === "identity" && (
          <Card key={`identity-${stepKey}`}>
            <CardHeader>
              <CardTitle>{t('students.multiStep.identity.title')}</CardTitle>
              <CardDescription>{t('students.multiStep.identity.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <StudentIdentityStep
                ref={identityStepRef}
                userId={studentData.userId}
                defaultValues={{
                  identityStatus: studentData.identityStatus,
                  signupSource: studentData.signupSource,
                  asaasCustomerId: studentData.asaasCustomerId,
                }}
                onSubmit={finishIdentity}
                onClose={onClose}
                onDirtyChange={onDirtyChange}
              />
            </CardContent>
          </Card>
        )}
      </div>

      <UnsavedChangesDialog
        open={dialogOpen}
        onSave={submitCurrentStep}
        onDiscard={discardAndGo}
        onCancel={stayHere}
      />
    </div>
  );
};
